/** @jsxImportSource @emotion/react */

import classNames from 'classnames';
import React from 'react';

export type UDTextType =
  | 'title-huge'
  | 'title-large'
  | 'title-1'
  | 'title-3'
  | 'headline'
  | 'body-text'
  | 'callout'
  | 'subhead'
  | 'footnote'
  | 'caption-1'
  | 'caption-2'
  | 'tip';

/**
 * Целевая шкала из макета (IMGM-1236), классы описаны в `src/styles/typography-scale.css`.
 * Имена совпадают с именами стилей в Figma — таблицы перевода между кодом и дизайном нет.
 */
export type UDTextVariant =
  | 'title-1'
  | 'title-2'
  | 'title-3'
  | 'title-4'
  | 'title-4-bold'
  | 'title-5'
  | 'title-6'
  | 'title-6-bold'
  | 'subtitle-1'
  | 'subtitle-1-bold'
  /** Вне дизайна — перенесён из `.text-new`, ждёт решения дизайнера. См. typography-scale.css. */
  | 'subtitle-2'
  | 'subtitle-2-bold'
  | 'subtitle-4'
  | 'body-1'
  | 'body-2'
  | 'body-3'
  | 'text-1'
  | 'text-2'
  | 'text-2-bold'
  | 'text-3';

export type UDTextTag = 'p' | 'span' | 'div' | 'label' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const componentsMap: Record<UDTextType, keyof JSX.IntrinsicElements> = {
  'title-huge': 'h1',
  'title-large': 'h1',
  'title-1': 'h1',
  'title-3': 'h3',
  headline: 'h1',
  'body-text': 'p',
  callout: 'p',
  subhead: 'p',
  footnote: 'p',
  'caption-1': 'p',
  'caption-2': 'p',
  tip: 'p',
};

type ComponentProps =
  | React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>
  | React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>;

type CommonProps = {
  maxWidth?: number;
  children: React.ReactNode;
  color?: string | 'inherit' | undefined;
  center?: boolean;
} & Omit<ComponentProps, 'children'>;

export type Props =
  | ({
      /** Легаси-шкала, уезжает по модулям в Фазе 3. */
      type: UDTextType;
    } & CommonProps)
  | ({
      /** Целевая шкала. Размер шрифта не диктует уровень заголовка — семантика задаётся через `as`. */
      variant: UDTextVariant;
      as?: UDTextTag;
      /** Межстрочный интервал для многострочного текста: в макете он равен кеглю. */
      relaxed?: boolean;
    } & CommonProps);

/**
 * Плоская проекция `Props` для разбора внутри компонента: ветки объединения не имеют общих
 * ключей, поэтому деструктурировать напрямую из `Props` нельзя. Наружу не выходит.
 */
type ResolvedProps = CommonProps & {
  type?: UDTextType;
  variant?: UDTextVariant;
  as?: UDTextTag;
  relaxed?: boolean;
};

const UDText = React.forwardRef<HTMLElement, Props>((props, ref) => {
  const { type, variant, as, relaxed, children, className, color, maxWidth, center, ...otherProps } = props as ResolvedProps;

  const additionalStyle = maxWidth
    ? {
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }
    : {};

  const Component = (variant ? (as ?? 'p') : componentsMap[type as UDTextType]) as keyof JSX.IntrinsicElements;

  const classes = variant
    ? classNames('typo', variant, className, { center, relaxed })
    : classNames('text', type, className, { center });

  return React.createElement(
    Component,
    {
      ...otherProps,
      ref,
      className: classes,
      style: { color, ...additionalStyle, ...otherProps.style },
    },
    children,
  );
});

export default UDText;
